import { CandidateCard } from "@/components/CandidateCard";
import { Candidate } from "@/types/candidate";
import { EntityGrid } from "@/components/common/EntityGrid";
import { useTranslation } from "react-i18next";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { candidateMergeConfig, CandidateForMerge } from "@/config/candidateMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { toast } from "@/hooks/use-toast";
import { CandidateMassUpdateModal } from "./CandidateMassUpdateModal";

interface CandidatesGridProps {
  candidates: Candidate[];
  filteredCandidates: Candidate[];
  isLoading: boolean;
  onDeleteCandidates?: (candidateIds: string[]) => Promise<void>;
  onCandidatesChange?: () => void;
  favorites?: Set<string>;
  cardMinWidth?: number;
}

const candidateFields = [
  "name",
  "email",
  "phone",
  "function",
  "skills",
  "match_score",
  "manager",
  "status",
  "ai_analysis",
  "ai_synthesis",
  "embedding",
  "linkedin",
  "linkedin_json",
  "location"
];

function normalizeCandidate(candidate) {
  const normalized = { ...candidate };
  candidateFields.forEach(field => {
    if (!(field in normalized)) {
      normalized[field] = "";
    }
  });
  return normalized;
}

export const CandidatesGrid = ({
  candidates,
  filteredCandidates,
  isLoading,
  onDeleteCandidates,
  onCandidatesChange,
  favorites = new Set(),
  cardMinWidth,
}: CandidatesGridProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isMassUpdateModalOpen, setIsMassUpdateModalOpen] = useState(false);

  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    clearSelection,
    isSelected,
    selectedCount,
    selectAll
  } = useMultiSelect(filteredCandidates, onDeleteCandidates || (() => Promise.resolve()));
  const normalizedCandidates = filteredCandidates.map(normalizeCandidate);

  const {
    openMergeModal,
    getDuplicateManagerProps
  } = useDuplicateManager(normalizedCandidates, isSelected, "candidates", candidateMergeConfig);
  // Handle candidate merge
  const handleMerge = async (merged: CandidateForMerge, sources: CandidateForMerge[]) => {
    try {
      const result = await EntityMergeService.mergeCandidates(merged, sources);      
      if (result.success) {
        toast({
          title: "Candidates merged successfully",
          description: `${sources.length} candidates have been merged into one.`,
        });
        // Refresh candidates list
        if (onCandidatesChange) {
          onCandidatesChange();
        }
        if (result.redirectPath)
          navigate(result.redirectPath);
        return result;
      } else {
        toast({
          title: "Failed to merge candidates",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
        // Rethrow error to prevent modal from closing
        throw new Error(result.error || "An error occurred during merge.");
      }
    } catch (error) {
      console.error("Merge error:", error);
      toast({
        title: "Failed to merge candidates",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
      // Rethrow error to prevent modal from closing
      throw error;
    }
  };

  // Mass update handlers
  const handleMassUpdate = () => {
    setIsMassUpdateModalOpen(true);
  };

  const handleMassUpdateComplete = () => {
    setIsMassUpdateModalOpen(false);
    clearSelection();
    onCandidatesChange?.();
  };

  const selectedItemIds = Array.from(
    new Set(
      filteredCandidates
        .filter(candidate => isSelected(candidate.id))
        .map(candidate => candidate.id)
    )
  );

  return (
    <>
      <EntityGrid
        data={normalizedCandidates}
        isLoading={isLoading}
        cardMinWidth={cardMinWidth}
        emptyMessage={t("candidates.list.empty")}
        renderItem={(candidate) => (
          <CandidateCard key={candidate.id} candidate={candidate} isSelected={isSelected(candidate.id)} isFavorite={favorites.has(candidate.id)} />
        )}
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        onMassUpdate={handleMassUpdate}
        selectedCount={selectedCount}
        selectAll={selectAll}
      />      <DuplicateManager
        {...getDuplicateManagerProps(handleMerge)}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CandidateMassUpdateModal
        isOpen={isMassUpdateModalOpen}
        onClose={() => setIsMassUpdateModalOpen(false)}
        selectedCandidateIds={selectedItemIds}
        onUpdate={handleMassUpdateComplete}
      />
    </>
  );
};
